import { useLazyQuery } from '@apollo/client';
import { QUERY_GET_FILTER_AUDIT_LOGS_FOR_ENTITY } from 'graphql/queries/businessEntities';
import { useEffect, useState, useRef } from 'react';
import { EntityAuditLog, EntityLogType, GetBusinessEntitySaasLogsInputPaginated, QueryGetFilterAuditLogsForEntity } from '../types';

export function useEntityAuditLogs(logType: EntityLogType, entityId: number, tenantId: number) {
    const [logs, setLogs] = useState<EntityAuditLog[]>([]);
    const [total, setTotal] = useState(0);
    const previousDataRef = useRef<string>('');

    const [findAuditLogs, { loading, data, fetchMore }] = useLazyQuery<
        QueryGetFilterAuditLogsForEntity,
        { data: GetBusinessEntitySaasLogsInputPaginated }
    >(QUERY_GET_FILTER_AUDIT_LOGS_FOR_ENTITY, {
        fetchPolicy: 'no-cache',
        variables: {
            data: {
                filterInput: {
                    entityId,
                    tenantId
                },
                pagination: { limit: 50, offset: 0 }
            }
        }
    });

    useEffect(() => {
        if (entityId && tenantId) {
            findAuditLogs();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entityId, tenantId]);

    useEffect(() => {
        if (data?.filterBusinessEntityAuditLogsForSaas) {
            const dataString = JSON.stringify(data.filterBusinessEntityAuditLogsForSaas);
            if (previousDataRef.current === dataString) {
                return;
            }
            previousDataRef.current = dataString;

            const processedLogs = data.filterBusinessEntityAuditLogsForSaas.logs;
            setLogs(processedLogs);
            setTotal(data.filterBusinessEntityAuditLogsForSaas.total);
        }
    }, [data]);

    const fetchMoreLogs = () => {
        if (!fetchMore) return;

        fetchMore({
            variables: {
                data: {
                    filterInput: {
                        entityId,
                        tenantId
                    },
                    pagination: {
                        limit: 25,
                        offset: logs.length
                    }
                }
            },
            updateQuery: (previousResult, { fetchMoreResult }) => {
                if (!fetchMoreResult?.filterBusinessEntityAuditLogsForSaas) {
                    return previousResult;
                }

                return {
                    filterBusinessEntityAuditLogsForSaas: {
                        logs: [
                            ...(previousResult.filterBusinessEntityAuditLogsForSaas?.logs || []),
                            ...fetchMoreResult.filterBusinessEntityAuditLogsForSaas.logs
                        ],
                        total: fetchMoreResult.filterBusinessEntityAuditLogsForSaas.total
                    }
                };
            }
        });
    };

    const refetchLogs = () => {
        if (entityId && tenantId) {
            findAuditLogs();
        }
    };

    return {
        loading,
        logs,
        total,
        fetchMoreLogs,
        refetchLogs
    };
}
